import { useState } from 'react';
import { api, ApiError, getToken, scr } from '../lib/api';
import { mdaName, pct } from '../lib/types';
import type { NbsExtract as NbsExtractData } from '../lib/types';
import { EmptyState, ErrorNote, PageHeader } from '../ui/components';

/**
 * NBS_PARTNER's entire surface: POST /jobs/nbs-extract, an aggregated,
 * k-anonymised cut of the fact tables (see service/extract.ts). There is no
 * raw-fact listing anywhere in this portal for this role — this page only
 * ever renders the same extract object the API returns, nothing per-case or
 * per-payment.
 */
export function NbsExtract() {
  const [data, setData] = useState<NbsExtractData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<NbsExtractData>('/api/v1/analytics/jobs/nbs-extract');
      setData(result);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to generate the extract');
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    setError(null);
    try {
      const token = getToken();
      const res = await fetch('/api/v1/analytics/jobs/nbs-extract?format=csv', {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nbs-extract-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download the CSV extract');
    }
  };

  return (
    <div>
      <PageHeader
        title="NBS extract"
        subtitle="Aggregated, k-anonymised cut for the National Bureau of Statistics — never raw per-case or per-payment records"
        actions={
          <div className="flex gap-2">
            {data ? (
              <button className="btn-secondary" onClick={downloadCsv}>
                Download CSV
              </button>
            ) : null}
            <button className="btn-primary" onClick={generate} disabled={busy}>
              {busy ? 'Generating…' : data ? 'Regenerate extract' : 'Generate extract'}
            </button>
          </div>
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {!data ? (
        <EmptyState
          title="No extract generated yet"
          hint="This calls the same aggregated, suppressed endpoint every time — there is no raw-fact access on this account."
        />
      ) : (
        <div>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Generated</div>
              <div className="mt-1 text-sm font-bold text-slate-900">{new Date(data.generatedAt).toLocaleString('en-GB')}</div>
            </div>
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Suppression threshold</div>
              <div className="mt-1 text-lg font-bold tabular-nums text-slate-900">&lt; {data.suppressionThreshold}</div>
            </div>
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cells suppressed</div>
              <div className="mt-1 text-lg font-bold tabular-nums text-amber-600">{data.suppressedCells}</div>
            </div>
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Digital adoption</div>
              <div className="mt-1 text-sm font-bold text-slate-900">
                cases {pct(data.adoption.casesDigitalRatioPct)} · usage {pct(data.adoption.serviceUsageDigitalRatioPct)}
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div>
              <h2 className="mb-2 text-sm font-bold text-slate-700">Cases by district</h2>
              <div className="card overflow-x-auto">
                {data.casesByDistrict.length === 0 ? (
                  <EmptyState title="No district reached the disclosure threshold" />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-head">District</th>
                        <th className="table-head text-right">Cases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.casesByDistrict.map((r) => (
                        <tr key={r.district}>
                          <td className="table-cell">{r.district}</td>
                          <td className="table-cell text-right tabular-nums">{r.count.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div>
              <h2 className="mb-2 text-sm font-bold text-slate-700">Service usage by district</h2>
              <div className="card overflow-x-auto">
                {data.usageByDistrict.length === 0 ? (
                  <EmptyState title="No district reached the disclosure threshold" />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-head">District</th>
                        <th className="table-head text-right">Uses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.usageByDistrict.map((r) => (
                        <tr key={r.district}>
                          <td className="table-cell">{r.district}</td>
                          <td className="table-cell text-right tabular-nums">{r.count.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div>
              <h2 className="mb-2 text-sm font-bold text-slate-700">Revenue by MDA</h2>
              <div className="card overflow-x-auto">
                {data.revenueByMda.length === 0 ? (
                  <EmptyState title="No revenue recorded" />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-head">MDA</th>
                        <th className="table-head text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.revenueByMda.map((r) => (
                        <tr key={`${r.mdaCode}-${r.currency}`}>
                          <td className="table-cell">{mdaName(r.mdaCode)}</td>
                          <td className="table-cell text-right tabular-nums">
                            {r.currency === 'SCR' ? scr(r.amount) : `${r.currency} ${r.amount.toLocaleString()}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <p className="mt-6 text-xs text-slate-400">
            This is the only data surface this account can reach — district cuts below {data.suppressionThreshold} records
            are never included (omitted, not zeroed), and there is no per-case or per-payment listing anywhere in this
            portal for the NBS Partner role.
          </p>
        </div>
      )}
    </div>
  );
}
